import { AutoComplete, Card, Checkbox, Input, Radio, Space, Typography, message } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { ComponentProps, ComponentType } from "react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import {
    Button as ZhButton,
    DatePicker as ZhDatePicker,
    Grid as ZhGrid,
    GridItem as ZhGridItem,
    InputNumber as ZhInputNumber,
    Select as ZhSelect
} from "zhihao-ui-react";

import { shippingRequestHttp } from "@/api";
import { serviceProviderConfigService } from "@/api/services/serviceProviderConfigService";
import { shipRouteService } from "@/api/services/shipRouteService";
import { wharfService } from "@/api/services/wharfService";
import { FREIGHT_RATE_TYPE, MARITIME_AFFAIRS_FEE } from "@/meta/logistics/shipNeed";
import { SHIP_WAY_BILL_TYPE } from "@/meta/logistics/waybill";
import { validatePhone } from "@/utils/validate";
import RequiredMark from "@/views/components/RequiredMark";
import { DEMAND_LEVEL, DRAINAGE_CHANNEL, ELECTRIC_CONTACT_NUMBER_TYPE, FIND_SHIP_TYPE } from "@zhihaoscm/meta";

export interface PlatformShipNeedFormData {
    ownerId?: number;
    ownerName?: string;
    requirementCustomerId?: number;
    serviceType?: number;
    ownerEnterprise?: { name?: string };
    contact?: string;
    mobile?: string;
    goodsType?: string;
    shipType?: number;
    drainageChannel?: number;
    sourcePortId?: number;
    sourcePortName?: string;
    destinationPortId?: number;
    destinationPortName?: string;
    shipRouteId?: number;
    shipRouteSource?: string;
    shipRouteDestination?: string;
    shipRouteName?: string;
    unitPriceType?: number;
    unitPrice?: number;
    tonMax?: number;
    tonMin?: number;
    freightTons?: number;
    loadDate?: string;
    loadDays?: number;
    loadUnloadDays?: number;
    demurrageFee?: number;
    maritimeAffairsFee?: number;
    replenishAppoint?: string;
    specialRemark?: string;
    deposit?: number;
    payType?: number;
    shipInfoServiceFee?: number;
    electricContactNumberType?: number;
    electricContactNumber?: string;
    demandLevel?: number;
    shipTonDemand?: number | boolean | null;
}

export interface PlatformShipNeedOption {
    value: number;
    label: string;
    source?: string;
    destination?: string;
}

export interface PlatformShipNeedFormPageProps {
    loadNeed?: (id: string) => Promise<PlatformShipNeedFormData>;
    createNeed?: (data: PlatformShipNeedFormData) => Promise<unknown>;
    updateNeed?: (id: string, data: PlatformShipNeedFormData) => Promise<unknown>;
    loadOwnerOptions?: (keyword: string) => Promise<PlatformShipNeedOption[]>;
    loadPortOptions?: (keyword: string) => Promise<PlatformShipNeedOption[]>;
    loadRouteOptions?: (sourcePortId: number, destinationPortId: number) => Promise<PlatformShipNeedOption[]>;
    loadProviderConfigs?: () => Promise<PlatformShipNeedOption[]>;
}

/** 复用 Vue 平台船运需求详情接口，为编辑表单提供回显数据。 */
export async function loadPlatformShipNeedForm(id: string): Promise<PlatformShipNeedFormData> {
    const response = await shippingRequestHttp.service.get<{ plat?: PlatformShipNeedFormData }>(
        `/shipping/plat/find/vo/${encodeURIComponent(id)}`
    );
    return response.data.plat || {};
}

/** 复用 Vue 新增平台船运需求接口。 */
export async function createPlatformShipNeed(data: PlatformShipNeedFormData) {
    const response = await shippingRequestHttp.service.post("/shipping/plat", data);
    return response.data;
}

/** 复用 Vue 编辑平台船运需求接口。 */
export async function updatePlatformShipNeed(id: string, data: PlatformShipNeedFormData) {
    const response = await shippingRequestHttp.service.put(`/shipping/plat/${encodeURIComponent(id)}`, data);
    return response.data;
}

const defaultProviderOptions: PlatformShipNeedOption[] = [
    { value: 2, label: "服务商垫付(黄码港网络货运平台)" },
    { value: 3, label: "服务商垫付(签好运网络货运平台)" }
];

/** 服务商配置沿用 Vue service-provider/config/find 接口。 */
async function loadPlatformProviderConfigs(): Promise<PlatformShipNeedOption[]> {
    const configs = await serviceProviderConfigService.find();
    const options = configs
        .filter(config => Number((config as unknown as { hasOpen?: number }).hasOpen) === 1)
        .map(config => {
            const item = config as unknown as { businessEnumCode?: number; pictureTextInfo?: { name?: string } };
            return { value: Number(item.businessEnumCode), label: item.pictureTextInfo?.name || "服务商" };
        })
        .filter(option => Number.isFinite(option.value));
    return options.length ? options : defaultProviderOptions;
}

/** 货主选择沿用 Vue role-select 的 customer/selector 查询契约。 */
async function loadPlatformOwnerOptions(keyword: string): Promise<PlatformShipNeedOption[]> {
    const response = await shippingRequestHttp.service.get<Array<Record<string, unknown>>>("/admin/customer/selector", {
        params: { param: keyword, personalAuth: 1 }
    });
    const customers = response.data;
    return customers
        .map(customer => {
            const item = customer as unknown as Record<string, unknown>;
            const value = Number(item.id);
            return { value, label: String(item.enterpriseName || item.name || item.realName || item.mobile || value) };
        })
        .filter(option => Number.isFinite(option.value));
}

/** 港口选择沿用 Vue remote-port-select 的 paging-selector 接口。 */
async function loadPlatformPortOptions(keyword: string): Promise<PlatformShipNeedOption[]> {
    const result = await wharfService.getPagingSelectorPort({ page: 1, size: 20, portName: keyword } as never);
    return result.list
        .map(port => {
            const item = port as unknown as Record<string, unknown>;
            return { value: Number(item.id), label: String(item.shortName || item.name || item.fullName || item.id) };
        })
        .filter(option => Number.isFinite(option.value));
}

/** 航线选择沿用 Vue 根据始发地/目的地查询航线的接口契约。 */
async function loadPlatformRouteOptions(sourcePortId: number, destinationPortId: number) {
    const routes = await shipRouteService.getRouteByStartAndEnd(sourcePortId, destinationPortId);
    return routes
        .map(route => {
            const item = route as unknown as Record<string, unknown>;
            const source = String(item.shipRouteSource || item.source || "");
            const destination = String(item.shipRouteDestination || item.destination || "");
            return {
                value: Number(item.id),
                label: String(item.name || item.routeName || `${source}-${destination}`),
                source,
                destination
            };
        })
        .filter(option => Number.isFinite(option.value));
}

const initialForm: PlatformShipNeedFormData = {
    ownerId: 1,
    serviceType: SHIP_WAY_BILL_TYPE.SUPERVISION.code,
    shipType: 1,
    drainageChannel: DRAINAGE_CHANNEL.DRAINAGE_CHANNEL_FAIR.code,
    maritimeAffairsFee: MARITIME_AFFAIRS_FEE.SHIPPERS_NOT_RESPONSIBLE.code,
    demandLevel: DEMAND_LEVEL.ONE.code,
    unitPriceType: FREIGHT_RATE_TYPE.PRICE_IS_FIXED.code,
    demurrageFee: 0,
    shipInfoServiceFee: 0,
    loadDays: 1,
    loadUnloadDays: 1,
    shipTonDemand: false,
    electricContactNumberType: ELECTRIC_CONTACT_NUMBER_TYPE.PHONE.code,
    payType: defaultProviderOptions[0].value
};

function numberValue(value: string): number | undefined {
    if (!value.trim()) return undefined;
    const result = Number(value);
    return Number.isFinite(result) ? result : undefined;
}

/** 对齐旧 Vue 手机号及手机/座机联系方式的格式校验。 */
export function validatePlatformShipNeedContact(
    form: Pick<PlatformShipNeedFormData, "mobile" | "electricContactNumberType" | "electricContactNumber">
): string {
    if (form.mobile?.trim() && !validatePhone(form.mobile.trim())) return "请输入正确的手机号格式";
    const contactNumber = form.electricContactNumber?.trim();
    if (!contactNumber) return "";
    if (form.electricContactNumberType === ELECTRIC_CONTACT_NUMBER_TYPE.PHONE.code) {
        return validatePhone(contactNumber) ? "" : "请输入正确的手机号码";
    }
    return /^(0\d{2,3}-)?\d{7,8}$/.test(contactNumber) ? "" : "请输入包含区号且保留“-”的座机号码";
}

const numberInputValue = (value: number | string | null | undefined) =>
    typeof value === "number" ? value : numberValue(String(value ?? ""));

// beta.13 运行时兼容旧 Vue MoneyInput 的无步进/精度配置，类型声明未暴露 controls。
type PlatformInputNumberProps = ComponentProps<typeof ZhInputNumber> & { controls?: boolean; precision?: number };
const PlatformInputNumber = ZhInputNumber as ComponentType<PlatformInputNumberProps>;

/** 平台船运需求新增/编辑表单，保留 Vue 字段、校验边界、远程选择和提交转换。 */
export function PlatformShipNeedFormPage({
    loadNeed = loadPlatformShipNeedForm,
    createNeed = createPlatformShipNeed,
    updateNeed = updatePlatformShipNeed,
    loadOwnerOptions = loadPlatformOwnerOptions,
    loadPortOptions = loadPlatformPortOptions,
    loadRouteOptions = loadPlatformRouteOptions,
    loadProviderConfigs = loadPlatformProviderConfigs
}: PlatformShipNeedFormPageProps) {
    const location = useLocation();
    const { id: routeId } = useParams();
    const isEdit = location.pathname.includes("platformShipNeedEdit");
    const id = routeId || location.pathname.split("/").filter(Boolean).pop() || "";
    const navigate = useNavigate();
    const [form, setForm] = useState<PlatformShipNeedFormData>(initialForm);
    const [errorMessage, setErrorMessage] = useState("");
    const [ownerOptions, setOwnerOptions] = useState<PlatformShipNeedOption[]>([]);
    const [sourcePortOptions, setSourcePortOptions] = useState<PlatformShipNeedOption[]>([]);
    const [destinationPortOptions, setDestinationPortOptions] = useState<PlatformShipNeedOption[]>([]);
    const [routeOptions, setRouteOptions] = useState<PlatformShipNeedOption[]>([]);
    const [providerOptions, setProviderOptions] = useState(defaultProviderOptions);
    const [providerPayMode, setProviderPayMode] = useState(form.payType === 1 ? 1 : 0);

    useEffect(() => {
        if (!isEdit || !id) return;
        let disposed = false;
        void loadNeed(id)
            .then(data => {
                if (!disposed) {
                    setForm(current => ({ ...current, ...data }));
                    setProviderPayMode(data.payType === 1 ? 1 : 0);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("船运需求回显失败");
            });
        return () => {
            disposed = true;
        };
    }, [id, isEdit, loadNeed]);

    useEffect(() => {
        let disposed = false;
        void loadProviderConfigs()
            .then(options => {
                if (!disposed && options.length) setProviderOptions(options);
            })
            .catch(() => undefined);
        return () => {
            disposed = true;
        };
    }, [loadProviderConfigs]);

    useEffect(() => {
        if (!form.sourcePortId || !form.destinationPortId) {
            setRouteOptions([]);
            return;
        }
        let disposed = false;
        void loadRouteOptions(form.sourcePortId, form.destinationPortId)
            .then(options => {
                if (!disposed) setRouteOptions(options);
            })
            .catch(() => {
                if (!disposed) setRouteOptions([]);
            });
        return () => {
            disposed = true;
        };
    }, [form.destinationPortId, form.sourcePortId, loadRouteOptions]);

    const setField = <K extends keyof PlatformShipNeedFormData>(key: K, value: PlatformShipNeedFormData[K]) => {
        setForm(current => ({ ...current, [key]: value }));
    };

    const searchOptions = async (
        keyword: string,
        loader: (value: string) => Promise<PlatformShipNeedOption[]>,
        setter: (options: PlatformShipNeedOption[]) => void
    ) => {
        try {
            setter(await loader(keyword));
        } catch {
            setter([]);
        }
    };

    const selectPort = (
        value: string,
        options: PlatformShipNeedOption[],
        nameKey: "sourcePortName" | "destinationPortName",
        idKey: "sourcePortId" | "destinationPortId"
    ) => {
        const selected = options.find(option => option.label === value);
        setField(nameKey, value);
        if (selected) setField(idKey, selected.value);
    };

    /** 保留旧表单输入边界，阻止无效 payload 发往接口。 */
    const validate = () => {
        if (!form.ownerId) return "请选择货主";
        if (!form.goodsType?.trim()) return "请输入货物类型";
        if (!form.freightTons || form.freightTons <= 0 || form.freightTons > 1000000)
            return "运货吨数应大于0且小于等于100万";
        if (!Number.isInteger(form.freightTons)) return "运货吨数应为整数";
        if (!form.sourcePortName?.trim()) return "请选择始发地";
        if (!form.destinationPortName?.trim()) return "请选择目的地";
        if (form.sourcePortId && form.destinationPortId && form.sourcePortId === form.destinationPortId)
            return "始发地和目的地不可为同一地";
        if (!form.contact?.trim()) return "请输入联系人";
        if (!form.mobile?.trim()) return "请输入手机号";
        const contactValidationError = validatePlatformShipNeedContact(form);
        if (contactValidationError) return contactValidationError;
        if (!form.loadDate?.trim()) return "请选择装载日期";
        if (form.loadDays === undefined || form.loadDays < 0 || form.loadDays > 30 || !Number.isInteger(form.loadDays))
            return "可宽限天数应为0至30的整数";
        if (
            form.loadUnloadDays === undefined ||
            form.loadUnloadDays <= 0 ||
            form.loadUnloadDays > 30 ||
            !Number.isInteger(form.loadUnloadDays)
        )
            return "装卸天数应大于0天且小于等于30天";
        if (form.tonMin !== undefined && form.tonMax !== undefined && form.tonMin > form.tonMax)
            return "最小吨位需小于最大吨位";
        if (
            form.unitPriceType === FREIGHT_RATE_TYPE.PRICE_IS_FIXED.code &&
            (!form.unitPrice || form.unitPrice <= 0 || form.unitPrice > 100000)
        )
            return "意向运价应大于0元且小于等于10万元";
        return "";
    };

    const submit = async () => {
        const validationError = validate();
        if (validationError) {
            setErrorMessage(validationError);
            return;
        }
        const payload: PlatformShipNeedFormData = {
            ...form,
            ownerEnterprise: form.ownerEnterprise || { name: form.ownerName },
            freightTons: Number(form.freightTons),
            tonMin: form.tonMin === undefined ? undefined : Number(form.tonMin),
            tonMax: form.tonMax === undefined ? undefined : Number(form.tonMax),
            unitPrice: form.unitPrice === undefined ? undefined : Number(form.unitPrice),
            loadDays: Number(form.loadDays || 0),
            loadUnloadDays: Number(form.loadUnloadDays || 0),
            demurrageFee: form.demurrageFee === undefined ? undefined : Number(form.demurrageFee),
            deposit: form.deposit === undefined ? undefined : Number(form.deposit),
            shipInfoServiceFee: form.shipInfoServiceFee === undefined ? undefined : Number(form.shipInfoServiceFee),
            shipTonDemand: form.shipTonDemand === false ? null : 1,
            payType: providerPayMode === 1 ? 1 : form.payType,
            requirementCustomerId: isEdit ? form.requirementCustomerId : undefined
        };
        try {
            if (isEdit) await updateNeed(id, payload);
            else await createNeed(payload);
            message.success(isEdit ? "修改成功" : "添加成功");
            navigate(-1);
        } catch {
            setErrorMessage(isEdit ? "平台船运需求修改失败" : "平台船运需求创建失败");
        }
    };

    const supervision = form.serviceType === SHIP_WAY_BILL_TYPE.SUPERVISION.code;
    const intentCosts = (Number(form.unitPrice) || 0) * (Number(form.freightTons) || 0);

    return (
        <section aria-label="platform-ship-need-form-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <Typography.Title className="mb-4 mt-0" level={4}>
                    {isEdit ? "修改船运需求" : "新增船运需求"}
                </Typography.Title>
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                <form
                    onSubmit={event => {
                        event.preventDefault();
                        void submit();
                    }}
                >
                    <ZhGrid cols={4} gap={[16, 0]}>
                        <ZhGridItem>
                            <fieldset>
                                <legend>服务类型</legend>
                                <Radio.Group
                                    aria-label="服务类型"
                                    value={form.serviceType}
                                    options={SHIP_WAY_BILL_TYPE.options.map(option => ({
                                        value: option.value,
                                        label: option.label
                                    }))}
                                    onChange={event => {
                                        const value = Number(event.target.value);
                                        setField("serviceType", value);
                                        if (value === SHIP_WAY_BILL_TYPE.MATCHING.code) {
                                            setProviderPayMode(1);
                                            setField("payType", 1);
                                        }
                                    }}
                                />
                            </fieldset>
                        </ZhGridItem>
                        <ZhGridItem>
                            <fieldset>
                                <legend>船运费用支付方式</legend>
                                {supervision ? (
                                    <Radio.Group
                                        aria-label="船运费用支付方式"
                                        value={providerPayMode}
                                        options={[
                                            { value: 0, label: "服务商垫付" },
                                            { value: 1, label: "自行支付" }
                                        ]}
                                        onChange={event => {
                                            const value = Number(event.target.value);
                                            setProviderPayMode(value);
                                            if (value === 1) setField("payType", 1);
                                        }}
                                    />
                                ) : (
                                    <span>自行支付</span>
                                )}
                            </fieldset>
                        </ZhGridItem>
                        <ZhGridItem>
                            <label>
                                服务商
                                <ZhSelect
                                    aria-label="服务商"
                                    className="w-full"
                                    disabled={!supervision || providerPayMode === 1}
                                    placeholder="请选择服务商"
                                    value={form.payType}
                                    options={providerOptions.map(option => ({
                                        value: option.value,
                                        label: option.label
                                    }))}
                                    onChange={value => setField("payType", Number(value))}
                                />
                            </label>
                        </ZhGridItem>
                        <ZhGridItem>
                            <label>
                                <span>
                                    <RequiredMark />
                                    货主名称
                                </span>
                                <AutoComplete
                                    aria-label="货主名称"
                                    className="w-full"
                                    options={ownerOptions}
                                    value={form.ownerName || form.ownerEnterprise?.name || ""}
                                    placeholder="请输入关键字选择货主"
                                    onSearch={keyword => void searchOptions(keyword, loadOwnerOptions, setOwnerOptions)}
                                    onChange={value => setField("ownerName", value)}
                                    onSelect={(value: string) => {
                                        const selected = ownerOptions.find(option => option.label === value);
                                        setField("ownerName", value);
                                        if (selected) setField("ownerId", selected.value);
                                    }}
                                />
                            </label>
                        </ZhGridItem>
                        <ZhGridItem span={4}>
                            <Typography.Text strong>货物信息</Typography.Text>
                        </ZhGridItem>
                        <ZhGridItem>
                            <label>
                                <span>
                                    <RequiredMark />
                                    货物类型
                                </span>
                                <Input
                                    aria-label="货物类型"
                                    maxLength={32}
                                    placeholder="请输入货物类型"
                                    value={form.goodsType || ""}
                                    onChange={event => setField("goodsType", event.target.value)}
                                />
                            </label>
                        </ZhGridItem>
                        <ZhGridItem>
                            <label>
                                <span>
                                    <RequiredMark />
                                    运货吨数
                                </span>
                                <PlatformInputNumber
                                    aria-label="运货吨数"
                                    className="w-full"
                                    controls={false}
                                    placeholder="请输入运货吨数"
                                    value={form.freightTons}
                                    onChange={value => setField("freightTons", numberInputValue(value))}
                                />
                            </label>
                        </ZhGridItem>
                        <ZhGridItem>
                            <label>
                                船型
                                <ZhSelect
                                    aria-label="船型"
                                    className="w-full"
                                    placeholder="请选择船型"
                                    value={form.shipType}
                                    options={FIND_SHIP_TYPE.options.map(option => ({
                                        value: option.value,
                                        label: option.label
                                    }))}
                                    onChange={value => setField("shipType", Number(value))}
                                />
                            </label>
                        </ZhGridItem>
                        <ZhGridItem>
                            <label>
                                排水槽
                                <ZhSelect
                                    aria-label="排水槽"
                                    className="w-full"
                                    placeholder="请选择排水槽"
                                    value={form.drainageChannel}
                                    options={DRAINAGE_CHANNEL.options.map(option => ({
                                        value: option.value,
                                        label: option.label
                                    }))}
                                    onChange={value => setField("drainageChannel", Number(value))}
                                />
                            </label>
                        </ZhGridItem>
                        <ZhGridItem>
                            <label>
                                <span>
                                    <RequiredMark />
                                    始发地
                                </span>
                                <AutoComplete
                                    aria-label="始发地"
                                    className="w-full"
                                    options={sourcePortOptions}
                                    value={form.sourcePortName || ""}
                                    placeholder="请输入码头名称或城市选择"
                                    onSearch={keyword =>
                                        void searchOptions(keyword, loadPortOptions, setSourcePortOptions)
                                    }
                                    onChange={value =>
                                        selectPort(value, sourcePortOptions, "sourcePortName", "sourcePortId")
                                    }
                                    onSelect={value =>
                                        selectPort(value, sourcePortOptions, "sourcePortName", "sourcePortId")
                                    }
                                />
                            </label>
                        </ZhGridItem>
                        <ZhGridItem>
                            <label>
                                <span>
                                    <RequiredMark />
                                    目的地
                                </span>
                                <AutoComplete
                                    aria-label="目的地"
                                    className="w-full"
                                    options={destinationPortOptions}
                                    value={form.destinationPortName || ""}
                                    placeholder="请输入码头名称或城市选择"
                                    onSearch={keyword =>
                                        void searchOptions(keyword, loadPortOptions, setDestinationPortOptions)
                                    }
                                    onChange={value =>
                                        selectPort(
                                            value,
                                            destinationPortOptions,
                                            "destinationPortName",
                                            "destinationPortId"
                                        )
                                    }
                                    onSelect={value =>
                                        selectPort(
                                            value,
                                            destinationPortOptions,
                                            "destinationPortName",
                                            "destinationPortId"
                                        )
                                    }
                                />
                            </label>
                        </ZhGridItem>
                        <ZhGridItem>
                            <label>
                                航线
                                <ZhSelect
                                    aria-label="航线"
                                    className="w-full"
                                    value={form.shipRouteId}
                                    placeholder="请选择航线"
                                    options={routeOptions.map(option => ({ value: option.value, label: option.label }))}
                                    onChange={value => {
                                        const selected = routeOptions.find(option => option.value === Number(value));
                                        setField("shipRouteId", Number(value));
                                        setField("shipRouteSource", selected?.source);
                                        setField("shipRouteDestination", selected?.destination);
                                        setField("shipRouteName", selected?.label);
                                    }}
                                />
                            </label>
                        </ZhGridItem>
                        <ZhGridItem>
                            <label>
                                最小吨位
                                <PlatformInputNumber
                                    aria-label="最小吨位"
                                    className="w-full"
                                    controls={false}
                                    value={form.tonMin}
                                    placeholder="请输入最小吨位"
                                    onChange={value => setField("tonMin", numberInputValue(value))}
                                />
                            </label>
                        </ZhGridItem>
                        <ZhGridItem>
                            <label>
                                最大吨位
                                <PlatformInputNumber
                                    aria-label="最大吨位"
                                    className="w-full"
                                    controls={false}
                                    value={form.tonMax}
                                    placeholder="请输入最大吨位"
                                    onChange={value => setField("tonMax", numberInputValue(value))}
                                />
                            </label>
                        </ZhGridItem>
                        <ZhGridItem>
                            <label>
                                随船吨位
                                <Checkbox
                                    checked={Boolean(form.shipTonDemand)}
                                    onChange={event => setField("shipTonDemand", event.target.checked)}
                                >
                                    随船
                                </Checkbox>
                            </label>
                        </ZhGridItem>
                        <ZhGridItem>
                            <label>
                                <span>
                                    <RequiredMark />
                                    期望装载日期
                                </span>
                                {/* Vue 的同一 form-item 中并排展示日期与宽限天数，避免迁移后拆成两个表单项。 */}
                                <Space.Compact className="w-full">
                                    <ZhDatePicker
                                        aria-label="装载日期"
                                        className="min-w-0 flex-1"
                                        type="date"
                                        format="YYYY-MM-DD"
                                        valueFormat="YYYY-MM-DD"
                                        value={form.loadDate}
                                        placeholder="请选择装载日期"
                                        onChange={(_, formattedValue) =>
                                            setField(
                                                "loadDate",
                                                typeof formattedValue === "string" ? formattedValue : undefined
                                            )
                                        }
                                    />
                                    <PlatformInputNumber
                                        aria-label="装载天数"
                                        className="min-w-0 flex-1"
                                        min={0}
                                        max={30}
                                        precision={0}
                                        controls={false}
                                        value={form.loadDays}
                                        placeholder="请选择宽限天数"
                                        onChange={value => setField("loadDays", numberInputValue(value))}
                                    />
                                </Space.Compact>
                            </label>
                        </ZhGridItem>
                        <ZhGridItem>
                            <label>
                                <span>
                                    <RequiredMark />
                                    装卸天数
                                </span>
                                <Input
                                    aria-label="装卸天数"
                                    placeholder="请输入装卸天数"
                                    value={form.loadUnloadDays ?? ""}
                                    onChange={event =>
                                        setField(
                                            "loadUnloadDays",
                                            numberValue(event.target.value.replace(/[^\d]/g, ""))
                                        )
                                    }
                                />
                            </label>
                        </ZhGridItem>
                        <ZhGridItem span={4}>
                            <Typography.Text strong>费用信息</Typography.Text>
                        </ZhGridItem>
                        <ZhGridItem span={2}>
                            <fieldset>
                                <legend>意向运价类型</legend>
                                <Radio.Group
                                    aria-label="意向运价类型"
                                    value={form.unitPriceType}
                                    options={FREIGHT_RATE_TYPE.options.map(option => ({
                                        value: option.value,
                                        label: option.label
                                    }))}
                                    onChange={event => setField("unitPriceType", Number(event.target.value))}
                                />
                            </fieldset>
                            {form.unitPriceType === FREIGHT_RATE_TYPE.PRICE_IS_FIXED.code ? (
                                <label>
                                    <span>
                                        <RequiredMark />
                                        意向运价
                                    </span>
                                    <PlatformInputNumber
                                        aria-label="意向运价"
                                        className="w-full"
                                        controls={false}
                                        precision={2}
                                        value={form.unitPrice}
                                        onChange={value => setField("unitPrice", numberInputValue(value))}
                                    />
                                </label>
                            ) : null}
                        </ZhGridItem>
                        {supervision ? (
                            <>
                                <ZhGridItem>
                                    <label>
                                        可接受滞期费
                                        <PlatformInputNumber
                                            aria-label="可接受滞期费"
                                            className="w-full"
                                            controls={false}
                                            precision={2}
                                            placeholder="请输入可接受滞期费"
                                            value={form.demurrageFee}
                                            onChange={value => setField("demurrageFee", numberInputValue(value))}
                                        />
                                    </label>
                                </ZhGridItem>
                                <ZhGridItem>
                                    <label>
                                        海事费用
                                        <ZhSelect
                                            aria-label="海事费用"
                                            className="w-full"
                                            value={form.maritimeAffairsFee}
                                            placeholder="请输入海事费用"
                                            options={MARITIME_AFFAIRS_FEE.options.map(option => ({
                                                value: option.value,
                                                label: option.label
                                            }))}
                                            onChange={value => setField("maritimeAffairsFee", Number(value))}
                                        />
                                    </label>
                                </ZhGridItem>
                                <ZhGridItem>
                                    <label>
                                        船运定金
                                        <PlatformInputNumber
                                            aria-label="船运定金"
                                            className="w-full"
                                            controls={false}
                                            precision={2}
                                            placeholder="请输入定金金额"
                                            value={form.deposit}
                                            onChange={value => setField("deposit", numberInputValue(value))}
                                        />
                                    </label>
                                </ZhGridItem>
                                <ZhGridItem>
                                    <label>
                                        船务信息服务费
                                        <PlatformInputNumber
                                            aria-label="船务信息服务费"
                                            className="w-full"
                                            controls={false}
                                            precision={2}
                                            placeholder="请输入船务信息服务费"
                                            value={form.shipInfoServiceFee}
                                            onChange={value => setField("shipInfoServiceFee", numberInputValue(value))}
                                        />
                                    </label>
                                </ZhGridItem>
                            </>
                        ) : null}
                        <ZhGridItem>
                            <label>
                                意向运费<span aria-label="意向运费合计">{intentCosts} 元</span>
                            </label>
                        </ZhGridItem>
                        <ZhGridItem span={4}>
                            <Typography.Text strong>联系方式</Typography.Text>
                        </ZhGridItem>
                        <ZhGridItem>
                            <label>
                                <span>
                                    <RequiredMark />
                                    联系人
                                </span>
                                <Input
                                    aria-label="联系人"
                                    placeholder="请输入联系人"
                                    value={form.contact || ""}
                                    onChange={event => setField("contact", event.target.value)}
                                />
                            </label>
                        </ZhGridItem>
                        <ZhGridItem>
                            <label>
                                <span>
                                    <RequiredMark />
                                    手机号
                                </span>
                                <Input
                                    aria-label="手机号"
                                    placeholder="请输入手机号"
                                    value={form.mobile || ""}
                                    onChange={event => setField("mobile", event.target.value)}
                                />
                            </label>
                        </ZhGridItem>
                        <ZhGridItem span={4}>
                            <label>
                                货主补充约定
                                <Input.TextArea
                                    aria-label="货主补充约定"
                                    maxLength={200}
                                    rows={3}
                                    value={form.replenishAppoint || ""}
                                    onChange={event => setField("replenishAppoint", event.target.value)}
                                />
                            </label>
                        </ZhGridItem>
                        <ZhGridItem>
                            <label>
                                电话号码
                                <Space.Compact className="w-full">
                                    <ZhSelect
                                        aria-label="电话号码类型"
                                        value={form.electricContactNumberType}
                                        options={ELECTRIC_CONTACT_NUMBER_TYPE.options.map(option => ({
                                            value: option.value,
                                            label: option.label
                                        }))}
                                        onChange={value => setField("electricContactNumberType", Number(value))}
                                    />
                                    <Input
                                        aria-label="电话号码"
                                        placeholder="请输入电话号码"
                                        value={form.electricContactNumber || ""}
                                        onChange={event => setField("electricContactNumber", event.target.value)}
                                    />
                                </Space.Compact>
                            </label>
                        </ZhGridItem>
                        <ZhGridItem>
                            <label>
                                需求等级
                                <ZhSelect
                                    aria-label="需求等级"
                                    className="w-full"
                                    value={form.demandLevel}
                                    options={DEMAND_LEVEL.options.map(option => ({
                                        value: option.value,
                                        label: option.label
                                    }))}
                                    onChange={value => setField("demandLevel", Number(value))}
                                />
                            </label>
                        </ZhGridItem>
                        <ZhGridItem span={4}>
                            <Typography.Text strong>特殊说明</Typography.Text>
                            <Input.TextArea
                                aria-label="特殊说明"
                                maxLength={200}
                                rows={3}
                                value={form.specialRemark || ""}
                                placeholder="请输入处理特殊说明"
                                onChange={event => setField("specialRemark", event.target.value)}
                            />
                        </ZhGridItem>
                        <ZhGridItem span={4}>
                            <Space className="operation-fixed-btn mt-6">
                                <ZhButton aria-label="提交" htmlType="submit" type="primary">
                                    提交
                                </ZhButton>
                                <ZhButton aria-label="返回" onClick={() => navigate(-1)}>
                                    返回
                                </ZhButton>
                            </Space>
                        </ZhGridItem>
                    </ZhGrid>
                </form>
            </Card>
        </section>
    );
}

export default PlatformShipNeedFormPage;
